import React, { useEffect, useRef } from 'react';
import { cn } from '../utils';

// ─── Time Picker Popover ──────────────────────────────────────────────────────
// Drum-roll style column picker — hour, minute, (second), (AM/PM)
// Mirrors CalendarPopover's container styling for visual consistency.

type TimeConstraint = { hour: number; minute: number; second: number };

interface TimePickerPopoverProps {
  hour: number; // 0–23
  minute: number;
  second: number;
  granularity: 'hour' | 'minute' | 'second';
  hourCycle: 12 | 24;
  onChange: (hour: number, minute: number, second: number) => void;
  minTime?: TimeConstraint;
  maxTime?: TimeConstraint;
}

const ITEM_H = 36; // px per row

function Column({
  items,
  selectedIndex,
  onSelect,
  disabledIndices = [],
}: {
  items: string[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  disabledIndices?: number[];
}) {
  const listRef = useRef<HTMLDivElement>(null);

  // Scroll selected item into view (center it)
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const domIndex = items.length - 1 - selectedIndex;
    const target = el.children[domIndex] as HTMLElement | undefined;
    if (target) {
      el.scrollTop = target.offsetTop - el.clientHeight / 2 + ITEM_H / 2;
    }
  }, [selectedIndex, items.length]);

  const wrap = (i: number) => ((i % items.length) + items.length) % items.length;

  return (
    <div
      ref={listRef}
      role="listbox"
      tabIndex={0}
      className="flex flex-col overflow-y-auto h-45 scrollbar-none snap-y snap-mandatory outline-none"
      style={{ scrollbarWidth: 'none' }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          onSelect(wrap(selectedIndex + 1));
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          onSelect(wrap(selectedIndex - 1));
        }
      }}
    >
      {/* Padding rows so first/last items can be centred */}
      <div className="shrink-0" style={{ height: ITEM_H * 2 }} />
      {[...items].reverse().map((label, ri) => {
        const i = items.length - 1 - ri;
        const isDisabled = disabledIndices.includes(i);
        return (
          <button
            key={i}
            type="button"
            onClick={() => onSelect(i)}
            className={cn(
              'shrink-0 flex items-center justify-center text-14 rounded-md transition-colors snap-center cursor-pointer',
              i === selectedIndex
                ? isDisabled
                  ? 'bg-red-bg text-red-text font-medium'
                  : 'bg-brand-2 text-brand-text font-medium'
                : isDisabled
                  ? 'opacity-30 hover:bg-neutral-2'
                  : 'text-neutral-alpha-9 hover:bg-neutral-2'
            )}
            style={{ height: ITEM_H }}
          >
            {label}
          </button>
        );
      })}
      <div className="shrink-0" style={{ height: ITEM_H * 2 }} />
    </div>
  );
}

const TimePickerPopover = React.forwardRef<HTMLDivElement, TimePickerPopoverProps>(function TimePickerPopover(
  { hour, minute, second, granularity, hourCycle, onChange, minTime, maxTime },
  ref
) {
  const isPM = hour >= 12;
  const h12 = hour % 12 || 12;

  const hours =
    hourCycle === 12
      ? Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'))
      : Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
  const seconds = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
  const periods = ['AM', 'PM'];

  const hourIndex = hourCycle === 12 ? h12 - 1 : hour;
  const minuteIndex = minute;
  const secondIndex = second;

  // Compute disabled indices
  const minH24 = minTime?.hour ?? 0;
  const maxH24 = maxTime?.hour ?? 23;
  const disabledHours = hours
    .map((_, i) => {
      const h24 = hourCycle === 12 ? (isPM ? (i + 1 === 12 ? 12 : i + 13) : i + 1 === 12 ? 0 : i + 1) : i;
      return h24 < minH24 || h24 > maxH24;
    })
    .reduce<number[]>((acc, d, i) => {
      if (d) acc.push(i);
      return acc;
    }, []);

  const hourDisabled = disabledHours.includes(hourIndex);
  const minM = hour === minTime?.hour ? (minTime?.minute ?? 0) : 0;
  const maxM = hour === maxTime?.hour ? (maxTime?.minute ?? 59) : 59;
  const disabledMinutes = hourDisabled
    ? minutes.map((_, i) => i)
    : minutes.reduce<number[]>((acc, _, i) => {
        if (i < minM || i > maxM) acc.push(i);
        return acc;
      }, []);

  const minuteDisabled = disabledMinutes.includes(minuteIndex);
  const minS = hour === minTime?.hour && minute === minTime?.minute ? (minTime?.second ?? 0) : 0;
  const maxS = hour === maxTime?.hour && minute === maxTime?.minute ? (maxTime?.second ?? 59) : 59;
  const disabledSeconds = minuteDisabled
    ? seconds.map((_, i) => i)
    : seconds.reduce<number[]>((acc, _, i) => {
        if (i < minS || i > maxS) acc.push(i);
        return acc;
      }, []);

  // Disable a period if the current time in that period is outside min/max
  const h12AsAM = h12 === 12 ? 0 : h12;
  const h12AsPM = h12 === 12 ? 12 : h12 + 12;
  const isOutOfRange = (h24: number) => {
    if (minTime) {
      if (h24 < minTime.hour) return true;
      if (h24 === minTime.hour && minute < minTime.minute) return true;
      if (h24 === minTime.hour && minute === minTime.minute && second < minTime.second) return true;
    }
    if (maxTime) {
      if (h24 > maxTime.hour) return true;
      if (h24 === maxTime.hour && minute > maxTime.minute) return true;
      if (h24 === maxTime.hour && minute === maxTime.minute && second > maxTime.second) return true;
    }
    return false;
  };
  const disabledPeriods: number[] = [];
  if (isOutOfRange(h12AsAM)) disabledPeriods.push(0);
  if (isOutOfRange(h12AsPM)) disabledPeriods.push(1);

  const setHour = (i: number) => {
    const val = i + (hourCycle === 12 ? 1 : 0);
    let h = hourCycle === 12 ? (isPM ? (val === 12 ? 12 : val + 12) : val === 12 ? 0 : val) : i;
    onChange(h, minute, second);
  };

  const setMinute = (i: number) => onChange(hour, i, second);
  const setSecond = (i: number) => onChange(hour, minute, i);
  const setPeriod = (i: number) => {
    const wantsPM = i === 1;
    let h = hour;
    if (wantsPM && h < 12) h += 12;
    if (!wantsPM && h >= 12) h -= 12;
    onChange(h, minute, second);
  };

  return (
    <div
      ref={ref}
      tabIndex={-1}
      className="bg-white border border-neutral-5 rounded-lg shadow-lg p-3 animate-in fade-in-0 zoom-in-95 outline-none"
    >
      <div className="flex gap-1 w-70">
        <div className="flex-1">
          <p className="text-12 font-medium text-neutral-alpha-7 text-center mb-1">Hr</p>
          <Column items={hours} selectedIndex={hourIndex} onSelect={setHour} disabledIndices={disabledHours} />
        </div>

        {(granularity === 'minute' || granularity === 'second') && (
          <>
            <div className="w-px bg-neutral-5 self-stretch mx-0.5" />
            <div className="flex-1">
              <p className="text-12 font-medium text-neutral-alpha-7 text-center mb-1">Min</p>
              <Column
                items={minutes}
                selectedIndex={minuteIndex}
                onSelect={setMinute}
                disabledIndices={disabledMinutes}
              />
            </div>
          </>
        )}

        {granularity === 'second' && (
          <>
            <div className="w-px bg-neutral-5 self-stretch mx-0.5" />
            <div className="flex-1">
              <p className="text-12 font-medium text-neutral-alpha-7 text-center mb-1">Sec</p>
              <Column
                items={seconds}
                selectedIndex={secondIndex}
                onSelect={setSecond}
                disabledIndices={disabledSeconds}
              />
            </div>
          </>
        )}

        {hourCycle === 12 && (
          <>
            <div className="w-px bg-neutral-5 self-stretch mx-0.5" />
            <div className="flex-1">
              <p className="text-12 font-medium text-neutral-alpha-7 text-center mb-1">&nbsp;</p>
              <Column
                items={periods}
                selectedIndex={isPM ? 1 : 0}
                onSelect={setPeriod}
                disabledIndices={disabledPeriods}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
});

export default TimePickerPopover;
